import { apiClient } from '../../../shared/api/client.js';
import type { User, Role } from '../../../shared/types/index.js';

export const authApi = {
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    return apiClient('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
    });
  },

  async register(data: {
    name: string;
    email: string;
    role: Role | string;
    password?: string;
    organization?: string;
    publisherCode?: string;
  }): Promise<{ user: User; token: string; message: string }> {
    return apiClient('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getMe(): Promise<{ user: User }> {
    return apiClient('/api/auth/me');
  },

  async getDemoAccounts(): Promise<{ accounts: User[] }> {
    return apiClient('/api/auth/demo-accounts');
  },

  async updateProfile(data: {
    name?: string;
    organization?: string;
    publisherCode?: string;
    profileImageUrl?: string;
    currentPassword?: string;
    newPassword?: string;
  }): Promise<{ user: User; message: string; token?: string }> {
    return apiClient('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async uploadProfileImage(file: File): Promise<{ profileImageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient('/api/auth/upload-profile-image', {
      method: 'POST',
      body: formData,
    });
  },

  async getAllUsers(search?: string, limit: number = 20, offset: number = 0): Promise<{ users: User[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    return apiClient(`/api/users?${params.toString()}`);
  },

  async getUsers(search?: string, limit: number = 20, offset: number = 0): Promise<{ users: User[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    return apiClient(`/api/users?${params.toString()}`);
  },

  async getPublishers(): Promise<{ publishers: Array<{ id: string; name: string; publisherCode: string }> }> {
    return apiClient('/api/users/publishers');
  },

  async createUser(data: {
    name: string;
    email: string;
    password?: string;
    role: Role | string;
    organization?: string;
    publisherCode?: string;
  }): Promise<{ user: User; message: string }> {
    return apiClient('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateUserAdmin(id: string, data: {
    name?: string;
    role?: Role | string;
    organization?: string;
    publisherCode?: string;
  }): Promise<{ user: User; message: string }> {
    return apiClient(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async updateUserRole(id: string, role: string): Promise<{ user: User; message: string }> {
    return apiClient(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  },

  async submitPublisherVerification(data: {
    legalName: string;
    nib?: string;
    phone: string;
    address: string;
    website?: string;
  }): Promise<{ message: string; user: User }> {
    return apiClient('/api/users/verify-publisher', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async verifyPublisherAdmin(
    id: string,
    status: 'verified' | 'rejected' | 'pending' | 'unverified',
    notes?: string
  ): Promise<{ message: string; user: User }> {
    return apiClient(`/api/users/${id}/verify-publisher`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    });
  },

  async deleteUser(id: string): Promise<{ message: string }> {
    return apiClient(`/api/users/${id}`, {
      method: 'DELETE',
    });
  },
};
